import { useEffect, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { AmbientBackground } from "@/components/home/AmbientBackground";

const EASE = [0.22, 1, 0.36, 1];

// Reduced-intensity ambient backdrop for public sub-pages (Level 2/3). Sits behind page content.
export function PublicAmbient({ intensity = 0.7 }) {
  return (
    <div className="absolute inset-0 -z-0 pointer-events-none" style={{ opacity: intensity }} aria-hidden>
      <AmbientBackground showScan={intensity > 0.6} />
    </div>
  );
}

// Orange -> amber -> warm metallic gold moving gradient text (gold as travelling glint).
export function GoldText({ children, className = "" }) {
  return <span className={`text-gradient-sweep ${className}`}>{children}</span>;
}

// Kinetic heading: staggered word entrance + travelling white/silver/gold light-sweep on the white words.
export function KineticHeading({ text, accent, as: Tag = "h1", className = "", shimmer = true }) {
  const reduce = useReducedMotion();
  const words = String(text).split(" ");
  return (
    <Tag className={`font-heading font-extrabold tracking-tighter text-donkey-warm ${shimmer ? "headline-glow" : ""} ${className}`}>
      <span className="[perspective:800px]">
        {words.map((w, i) => (
          <motion.span
            key={w + i}
            className={`inline-block mr-[0.24em] ${shimmer ? "headline-shimmer" : ""}`}
            style={shimmer && !reduce ? { animationDelay: `${i * 0.1}s` } : undefined}
            initial={reduce ? false : { opacity: 0, y: 22, rotateX: -60, filter: "blur(5px)" }}
            whileInView={{ opacity: 1, y: 0, rotateX: 0, filter: "blur(0px)" }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.55, delay: 0.04 + i * 0.06, ease: EASE }}
          >
            {w}
          </motion.span>
        ))}
        {accent && (
          <motion.span
            className="inline-block"
            initial={reduce ? false : { opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.5, delay: 0.05 + words.length * 0.06 }}
            style={{ filter: "drop-shadow(0 0 16px rgba(249,115,22,0.4))" }}
          >
            <GoldText>{accent}</GoldText>
          </motion.span>
        )}
      </span>
    </Tag>
  );
}

// Meaningful scroll reveal — direction communicates the story (from where the content "comes").
export function Reveal({ children, x = 0, y = 28, delay = 0, amount = 0.3, once = true, className = "" }) {
  const reduce = useReducedMotion();
  return (
    <motion.div
      className={className}
      initial={reduce ? false : { opacity: 0, x, y }}
      whileInView={{ opacity: 1, x: 0, y: 0 }}
      viewport={{ once, amount }}
      transition={{ duration: 0.55, delay, ease: EASE }}
    >
      {children}
    </motion.div>
  );
}

// Rotating sceptical Donkey observation — personality layer for public pages.
export function DonkeyQuote({ quotes, interval = 3600, className = "" }) {
  const reduce = useReducedMotion();
  const [i, setI] = useState(0);
  useEffect(() => {
    if (reduce || quotes.length < 2) return;
    const id = setInterval(() => setI((p) => (p + 1) % quotes.length), interval);
    return () => clearInterval(id);
  }, [reduce, quotes.length, interval]);
  return (
    <div className={`flex items-start gap-3 ${className}`} data-testid="donkey-quote">
      <span className="mt-1.5 w-2 h-2 rounded-full bg-donkey-orange animate-pulse-soft shrink-0" aria-hidden />
      <div className="relative min-h-[2.4rem] flex-1">
        <AnimatePresence mode="wait">
          <motion.p
            key={i}
            initial={reduce ? false : { opacity: 0, y: 10, filter: "blur(3px)" }}
            animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            exit={reduce ? undefined : { opacity: 0, y: -10, filter: "blur(3px)" }}
            transition={{ duration: 0.42, ease: EASE }}
            className="text-sm sm:text-base italic text-donkey-warm/80 leading-relaxed"
          >
            {quotes[i]}
          </motion.p>
        </AnimatePresence>
      </div>
    </div>
  );
}
